import React, { useEffect, memo } from 'react';
import { Box, Typography, TextField, CircularProgress } from '@mui/material';
import PhoneInputWithLabel from '../../../../core/components/ui/PhoneInputWithLabel';
import InfoBox from '../../../../core/components/ui/InfoBox';
import InfoAlert from '../../../../core/components/ui/InfoAlert';
import { StyledDropdown } from '../../../../core/components/ui/StyledDropdown';
import { SelectChangeEvent } from '@mui/material/Select';
import { usePartners } from '../../../../core/hooks/usePartners';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store';
import { useFormContext, Controller } from 'react-hook-form';

interface BasicContactStepProps {
  isMobile: boolean;
  isSaving?: boolean;
}

const BasicContactStep: React.FC<BasicContactStepProps> = ({
  isMobile,
  isSaving = false,
}) => {
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const { partners, isLoadingPartners, partnersError } = usePartners();

  const { control, formState: { errors }, reset } = useFormContext();

  const partnerOptions = [
    { value: 'NONE', label: 'None' },
    // ...(partners?.map((partner) => ({
    //   value: partner._id,
    //   label: partner.name,
    // })) || []),
  ];

  // IMPORTANT: Only run this effect once on mount or when dependencies actually change
  useEffect(() => {
    if (!basicInfo || isLoadingPartners) return;
    
    // Set this flag to prevent resetting during render
    let isInitialRender = true;
    
    if (isInitialRender) {
      reset({
        phoneNumber: basicInfo.phone?.replace(/^\+\d*(\d{10})$/, '$1') || '',
        partnerId: basicInfo.partnerId || 'NONE',
        memberSource: '',
      }, { keepDefaultValues: true });
      isInitialRender = false;
    }
  }, [basicInfo?.phone, basicInfo?.partnerId, basicInfo?.memberSource, isLoadingPartners, reset]);

  return (
    <Box>
      {!isMobile && (
        <Box>
          <Typography variant='h4' color='text.primary' sx={{ mb: 1 }}>
            Join MindRight Health
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
            Connect with a mental health coach via text
          </Typography>
        </Box>
      )}

      <InfoAlert>
        MindRight provides mental health coaching via text message. Messages are delivered to the
        phone number you provide below.
      </InfoAlert>

      <Box sx={{ mb: 3 }}>
        <Controller
          name='phoneNumber'
          control={control}
          render={({ field }) => (
            <PhoneInputWithLabel
              value={field.value || ''}
              onChange={(value) => field.onChange(value)}
              countryCodeEditable={false}
              disableCountryCode={true}
              label='Phone number'
              disabled={true}
              helperText={"This is where you’ll receive messages from MindRight"}
              error={Boolean(errors.phoneNumber)}
              required
            />
          )}
        />
      </Box>

      {/* Partner affiliation field – temporarily hidden, can be re-enabled when needed
      <Box sx={{ mb: 4 }}>
        <Controller
          name='partnerId'
          control={control}
          render={({ field }) => (
            <StyledDropdown
              label='Are you affiliated with one of our Partners?'
              name={field.name}
              value={field.value || 'NONE'}
              onChange={(e: SelectChangeEvent<string | string[]>) => {
                field.onChange(e.target.value);
              }}
              options={partnerOptions}
              helperText={'Select a partner or choose None'}
              data-id={'partner-single-selection-dropdown'}
              disabled={isLoadingPartners || isSaving}
              startAdornment={
                isLoadingPartners ? (
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                ) : undefined
              }
              error={Boolean(errors.partnerId) || !!partnersError}
            />
          )}
        />
      </Box>
      */}

      <Box sx={{ mb: 4 }}>
        <Controller
          name='memberSource'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='How did you hear about MindRight?'
              {...field}
              value={field.value || ''} 
              error={Boolean(errors.memberSource)}
              disabled={isSaving}
              variant='outlined'
              slotProps={{
                inputLabel: {
                  sx: {
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    overflow: 'visible', 
                    maxWidth: 'none',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.65)',
                    },
                    '@media (max-width: 600px)': {
                      fontSize: '0.6rem',
                      lineHeight: 1.1,
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.6)',
                      },
                    },
                  },
                },
              }}
            />
          )}
        />
      </Box>

      <InfoBox>
        Standard messaging rates may apply. You can text STOP at any time to opt out.
      </InfoBox>
    </Box>
  );
};

export default memo(BasicContactStep);